import { cloneAndFreezePlainData } from "../../core/lifecycle/kernel.ts";
import { fnv1a64 } from "../../core/determinism/canonical.ts";
import {
  ACTOR_KIND,
  OWNER_KIND,
  createLedgerState,
  type LedgerState,
} from "../transactions/index.ts";
import { MANUAL_CHAIN_POINTS, MANUAL_CHAIN_TUNING } from "./config.ts";
import type {
  ManualChainCargoKind,
  ManualChainEvent,
  ManualChainEventKind,
  ManualChainObjectiveId,
  ManualChainState,
  ManualChainPlot,
} from "./types.ts";

export function createEmptyActionLifecycleSlice(): ManualChainState["lifecycleSlice"] {
  return cloneAndFreezePlainData({
    lastPhase8Tick: null,
    currentOverlapFacts: [],
    actionStateSlots: [],
    actionCancellations: [],
    tendRecoveryTokens: [],
    actionSelectionRequests: [],
    unstartedGrantReleases: [],
    phase8Output: null,
    phase9: { status: "none" as const, authorityTick: null, output: null },
  });
}

function createInitialLedger(): LedgerState {
  return createLedgerState({
    items: [],
    owners: [
      { ownerKind: OWNER_KIND.ACTOR_CARGO, ownerId: 1, version: 0, capacity: 5, acceptedItemKinds: [1, 2], navRelevant: false },
      { ownerKind: OWNER_KIND.PEN_STAGING, ownerId: 2, version: 0, capacity: 8, acceptedItemKinds: [1], navRelevant: false },
      { ownerKind: OWNER_KIND.PEN_OUTPUT, ownerId: 2, version: 0, capacity: 8, acceptedItemKinds: [2], navRelevant: false },
      { ownerKind: OWNER_KIND.MARKET_SERVICE, ownerId: 3, version: 0, capacity: 8, acceptedItemKinds: [2], navRelevant: false },
    ],
    actors: [{ actorId: 1, actorKind: ACTOR_KIND.HUMANOID, actionId: 0, enabled: true, present: true }],
    currency: 0,
    navRevision: 0,
  });
}

export function createInitialManualChainState(): Readonly<ManualChainState> {
  const plots: ManualChainPlot[] = Array.from({ length: MANUAL_CHAIN_TUNING.plotCount }, (_, index) => ({
    id: index + 1,
    state: "ready",
    cooldownTicks: 0,
  }));
  return cloneAndFreezePlainData({
    tick: 0,
    player: {
      actorId: 1,
      bodyId: 11,
      present: true,
      bodyVisible: true,
      xMicroWU: MANUAL_CHAIN_POINTS.field.xMicroWU,
      zMicroWU: MANUAL_CHAIN_POINTS.field.zMicroWU,
      facingXMicroWU: 1_000_000,
      facingZMicroWU: 0,
      moving: false,
      cargoCapacity: 5,
    },
    plots,
    ledger: createInitialLedger(),
    lifecycleSlice: createEmptyActionLifecycleSlice(),
    activeAction: null,
    route: null,
    objectiveId: "pick-corn",
    penExtensionAvailable: false,
    arrivalEligibility: { penTick: null, marketTick: null },
    events: [],
    counters: {
      nextItemId: 1,
      nextActionId: 1,
      nextWorkOrderId: 2,
      nextIdempotencyKey: 1,
      nextEventId: 1,
    },
  } satisfies ManualChainState);
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (typeof value !== "object") throw new TypeError("manual-chain hash accepts plain data only");
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(",")}}`;
}

export function hashManualChainState(state: Readonly<ManualChainState>): string {
  return fnv1a64(new TextEncoder().encode(stableStringify(state)));
}

export function countOwnedItems(state: Readonly<ManualChainState>, ownerKind: number, ownerId: number, itemKind?: number): number {
  return state.ledger.items.filter((item) =>
    item.ownerKind === ownerKind && item.ownerId === ownerId && (itemKind === undefined || item.kind === itemKind),
  ).length;
}

export function cargoKind(state: Readonly<ManualChainState>): ManualChainCargoKind {
  const carried = state.ledger.items.filter((item) => item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1);
  if (carried.length === 0) return "none";
  return carried[0]!.kind === 1 ? "corn" : "egg";
}

export function deriveObjective(state: Readonly<ManualChainState>): ManualChainObjectiveId {
  if (state.ledger.currency >= 32) return "pen-extension-ready";
  const cargo = cargoKind(state);
  const carried = countOwnedItems(state, OWNER_KIND.ACTOR_CARGO, 1);
  const staged = countOwnedItems(state, OWNER_KIND.PEN_STAGING, 2, 1);
  const output = countOwnedItems(state, OWNER_KIND.PEN_OUTPUT, 2, 2);
  if (output > 0) return "collect-eggs";
  if (cargo === "egg") return "sell-eggs";
  if (staged > 0) return "feed-chickens";
  if (cargo === "corn" && carried >= 4) return "feed-chickens";
  return "pick-corn";
}

export function appendManualChainEvent(
  state: Readonly<ManualChainState>,
  event: Readonly<{
    kind: ManualChainEventKind;
    actorId: number;
    actionId: number;
    workOrderId: number;
    itemId?: number | null;
    plotId?: number | null;
    lineageId?: number | null;
    quantity?: number;
  }>,
): ManualChainState {
  const id = state.counters.nextEventId;
  const next: ManualChainEvent = Object.freeze({
    id,
    sequence: id,
    tick: state.tick,
    kind: event.kind,
    actorId: event.actorId,
    actionId: event.actionId,
    workOrderId: event.workOrderId,
    itemId: event.itemId ?? null,
    plotId: event.plotId ?? null,
    lineageId: event.lineageId ?? null,
    quantity: event.quantity ?? 0,
  });
  return {
    ...state,
    events: [...state.events, next],
    counters: { ...state.counters, nextEventId: id + 1 },
  };
}
